import { useMemo } from 'react';
import TopbarApplicationsMenu from './TopbarApplicationsMenu.jsx';
import TopbarSearch from './TopbarSearch.jsx';
import TopbarRightZone from './TopbarRightZone.jsx';
import { useTopbarApplicationSelection } from './useTopbarApplicationSelection.js';
import {
  topbarCenterZoneClass,
  topbarContainerClass,
  topbarHamburgerClass,
  topbarLeftZoneClass,
} from './topbarClasses.js';
import { Menu } from 'lucide-react';

const Topbar = ({
  studio,
  applications = [],
  selectedApplicationId: selectedApplicationIdProp,
  onSelectApplication,
  variant = 'default',
  showApplicationDescriptions = false,
  search = {},
  searchProps = {},
  onOpenSearch,
  actionButtons = [],
  brandMark: _brandMark,
  company,
  accountMenu,
  labels = {},
  tooltips = {},
  onMenuClick,
  className = '',
}) => {
  const onKeyDown = useMemo(() => {
    const submitFromProps = searchProps.onSubmit || search.onSubmit;
    if (!submitFromProps && !searchProps.onKeyDown) return undefined;
    return (event) => {
      searchProps.onKeyDown?.(event);
      if (event.key === 'Enter') {
        submitFromProps?.(event.target.value, event);
      }
    };
  }, [search.onSubmit, searchProps.onSubmit, searchProps.onKeyDown]);

  const resolvedSearchProps = useMemo(
    () => ({
      value: search.value,
      placeholder: search.placeholder,
      onChange: search.onChange
        ? (event) => search.onChange(event.target.value, event)
        : undefined,
      ...searchProps,
      onKeyDown,
    }),
    [search.value, search.placeholder, search.onChange, searchProps, onKeyDown],
  );

  const { selectedApplicationId, selectedApplication, handleSelectApplication } =
    useTopbarApplicationSelection(
      applications,
      selectedApplicationIdProp,
      onSelectApplication,
    );

  return (
    <header className={[topbarContainerClass, className].filter(Boolean).join(' ')}>
      <div className={topbarLeftZoneClass}>
        {onMenuClick ? (
          <button
            type="button"
            onClick={onMenuClick}
            className={topbarHamburgerClass}
            aria-label={labels.openMenu || 'Open menu'}
          >
            <Menu size={16} aria-hidden />
          </button>
        ) : null}
        <TopbarApplicationsMenu
          studio={studio}
          applications={applications}
          selectedApplicationId={selectedApplicationId}
          onSelectApplication={handleSelectApplication}
          showApplicationDescriptions={showApplicationDescriptions}
          labels={labels}
          studioTooltip={tooltips.studio}
          className="w-fit shrink-0"
        />
      </div>

      <div className={topbarCenterZoneClass}>
        <TopbarSearch
          variant="pill"
          searchProps={resolvedSearchProps}
          searchTooltip={tooltips.search}
          onOpenSearch={onOpenSearch}
        />
      </div>

      <TopbarRightZone
        searchProps={resolvedSearchProps}
        onOpenSearch={onOpenSearch}
        actionButtons={actionButtons}
        topbarVariant={variant}
        actionTooltips={tooltips.actions || {}}
        company={company}
        accountMenu={accountMenu}
        labels={labels}
        tooltips={tooltips}
        studio={studio}
        selectedApplication={selectedApplication}
      />
    </header>
  );
};

export default Topbar;
